import { FormattedMessage, useIntl } from 'react-intl';
import Input, { InputWithMenu } from '../Input';
import { useFormContext, useWatch } from 'react-hook-form';
import { OrderSendForm } from '@/types/order';
import { useEffect, useState } from 'react';
import useLocale from '@/hooks/useLocale';
import { countriesEnData } from '@/utils/countriesEnData';
import { countriesChData } from '@/utils/countriesChData';
import { countriesJaData } from '@/utils/countriesJaData';
import { countriesEsData } from '@/utils/countriesEsData';
import Button from '../Button';
import useToggle from '@/hooks/useToggle';
import ReceivingMethodModal from '@/components/ReceivingMethodModal';
import { useShippingCountries } from '@/hooks/useShippingCountries';
import { NotificationBadge } from '../NotificationBadge/NotificationBadge';
import OrderPageHeaderSection from './OrderPageHeaderSection';
import DaumPostcodeEmbed from 'react-daum-postcode';
import { useMutation } from '@tanstack/react-query';
import useApi from '@/hooks/useApi';
import { postStaffHelp } from '@/apis/main';
import { WebviewAction } from '@/utils/action';
import { TAX_ID_CONFIG } from '@/utils/taxValidation';
import { Image } from '@/components/Image';

const emailDomainData = ['gmail.com', 'hotmail.com', 'yahoo.com', 'icloud.com', 'Enter manually'];
const emailDomainRegexp = /^([\w-]+\.)+[\w-]{2,4}$/;

export const RecipientInfo = ({ isGlobal, isShippingAvailable }: { isGlobal: boolean; isShippingAvailable: boolean }) => {
  const intl = useIntl();
  const { locale } = useLocale();
  const { register, setValue, getValues } = useFormContext<OrderSendForm>();
  const countryValue = useWatch({ name: 'shipping_info_country_code' });
  const domainValue = useWatch({ name: 'shipping_info_email_2' });
  const { fetchApi } = useApi();
  const deviceId: string = WebviewAction() ?? 'kioskdev';

  const [emailDomainErrorMessage, setEmailDomainErrorMessage] = useState<string | undefined>();
  const [countriesDataArr, setCountriesDataArr] = useState(countriesEnData);
  const [isOptionVisible, setIsOptionVisible] = useState(false);
  const { countries: shippingCountries } = useShippingCountries();

  const staffHelpBadge = useToggle({ showing: false });
  const receivingModal = useToggle({ showing: false });
  const addressModal = useToggle({ showing: false });

  const { mutate } = useMutation({
    mutationFn: async () => {
      fetchApi((token) => postStaffHelp(deviceId, token));
    }
  });

  const { shipping_info_country_code } = getValues();

  const handleEmailDomainClick = (emailDomain: string) => {
    if (emailDomain === 'Enter manually') {
      setValue('shipping_info_email_2', '');
    } else {
      setValue('shipping_info_email_2', emailDomain);
    }
  };

  //국내 주소 선택시 저장 함수
  const handleComplete = (data: any) => {
    let fullAddress = data.address;
    let extraAddress = '';

    if (data.addressType === 'R') {
      if (data.bname !== '') {
        extraAddress += data.bname;
      }
      if (data.buildingName !== '') {
        extraAddress += extraAddress !== '' ? `, ${data.buildingName}` : data.buildingName;
      }
      fullAddress += extraAddress !== '' ? ` (${extraAddress})` : '';
    }

    setValue('shipping_info_address_1', data.address);
    setValue('shipping_info_postal_code', data.zonecode);

    addressModal.close();
  };

  //customer info 정보 불러오기 클릭시 함수
  const handleUseCustomerInformationClick = () => {
    const {
      customer_first_name,
      customer_last_name,
      customer_country_code,
      customer_country_code_number,
      customer_phone_number,
      customer_email_1,
      customer_email_2
    } = getValues();

    setValue('shipping_info_first_name', customer_first_name);
    setValue('shipping_info_last_name', customer_last_name);
    setValue('shipping_info_country_code', customer_country_code);
    setValue('shipping_info_country_code_number', customer_country_code_number ?? '');
    setValue('shipping_info_phone_number', customer_phone_number);
    setValue('shipping_info_email_1', customer_email_1);
    setValue('shipping_info_email_2', customer_email_2);
  };

  //국내유저면 shipping_info_country_code 한국으로 고정
  useEffect(() => {
    setValue('shipping_info_email_2', 'gmail.com');
    if (!isGlobal) {
      setValue('shipping_info_country_code', 'KR');
    }

    switch (locale) {
      case 'en':
        return setCountriesDataArr(countriesEnData);
      case 'zh':
        return setCountriesDataArr(countriesChData);
      case 'ja':
        return setCountriesDataArr(countriesJaData);
      case 'es':
        return setCountriesDataArr(countriesEsData);
    }
  }, []);

  useEffect(() => {
    setEmailDomainErrorMessage(
      emailDomainRegexp.test(domainValue) || domainValue === undefined ? '' : 'Please enter a correct email address.'
    );
  }, [domainValue]);

  return (
    <>
      <div className="flex flex-col gap-6 px-5 pb-10">
        <OrderPageHeaderSection
          title={`${intl.formatMessage({
            id: isGlobal ? 'recipient_information_international' : 'recipient_information_korea'
          })}`}
          isShippingAvailable={isShippingAvailable}
          onClick={receivingModal.open}
        />
        <div className="flex gap-3">
          <div
            className="border border-[#e0e0e0] text-[14px] font-bold rounded-[12px] py-2 px-4 flex items-center gap-2 cursor-pointer bg-white"
            onClick={handleUseCustomerInformationClick}
          >
            <Image src="/icons/rotate.svg" alt="rotate_icon" width={16} height={16} />
            <FormattedMessage id="use_customer_information" defaultMessage="Use Customer Information" />
          </div>
          {!isGlobal && (
            <div
              className="border border-[#e0e0e0] text-[14px] font-bold rounded-[12px] py-2 px-4 flex items-center gap-2 cursor-pointer bg-white"
              onClick={() => {
                staffHelpBadge.open();
                mutate();
              }}
              style={{ position: 'relative' }}
            >
              <Image src="/icons/my.svg" alt="rotate_icon" width={16} height={16} />
              <FormattedMessage id="request_staff_help" defaultMessage="Request Staff Help" />
              {staffHelpBadge.isShowing && (
                <NotificationBadge
                  position={{ top: 0, left: 200 }}
                  message={intl.formatMessage({
                    id: 'notified_a_staff_member',
                    defaultMessage: 'We’ve notified a staff member. Please wait a moment.'
                  })}
                  onClose={() => staffHelpBadge.close()}
                />
              )}
            </div>
          )}
        </div>
        <div className="flex flex-row gap-4 w-full">
          {/* First name */}
          <Input
            label={intl.formatMessage({ id: 'order_section1_input1_label', defaultMessage: 'First name' })}
            containerClassName="flex-1"
            placeholder={intl.formatMessage({ id: 'order_section1_input1_placeholder', defaultMessage: 'First name' })}
            register={register('shipping_info_first_name', { required: true })}
          />

          {/* Last name */}
          <Input
            label={intl.formatMessage({ id: 'order_section1_input2_label', defaultMessage: 'Last name' })}
            containerClassName="flex-1"
            placeholder={intl.formatMessage({ id: 'order_section1_input2_placeholder', defaultMessage: 'Last name' })}
            register={register('shipping_info_last_name', { required: true })}
          />
        </div>

        {/* Phone number (+ Tax ID 우측 컬럼: 일부 통관 대상국에서만 노출) */}
        <div className="relative flex flex-row gap-4">
          <InputWithMenu
            label={
              <div className="flex items-center gap-2 ">
                {intl.formatMessage({ id: 'order_section1_input3_label', defaultMessage: 'Phone number' })}
                {!isGlobal && (
                  <div className="text-[13px] font-semibold text-[#808080]">
                    <FormattedMessage
                      id="for_delivery_within_korea"
                      defaultMessage="(For delivery within Korea, please enter a Korean phone number.)"
                    />
                  </div>
                )}
              </div>
            }
            menus={isGlobal ? shippingCountries : countriesDataArr}
            inputContainerClassName={isGlobal && TAX_ID_CONFIG[countryValue] ? 'w-[280px]' : 'w-[533px]'}
            placeholder={intl.formatMessage({ id: 'order_section1_input3_input-placeholder', defaultMessage: 'Phone number' })}
            register={register('shipping_info_phone_number', { required: true })}
            menuValue={countryValue}
            onMenuSelect={(code, codeNumber) => {
              setValue('shipping_info_country_code', code);
              setValue('shipping_info_country_code_number', codeNumber);
              setValue('shipping_info_clearance_code', '');
            }}
            type={'number'}
          />

          {!isGlobal && (
            <div className="absolute w-[320px] h-[54px] top-7 flex justify-between z-[5] text-[16px] font-medium text-[#999999] bg-[#ededed] border border-[#e0e0e0] py-4 px-5 rounded-[10px]">
              <span>South Korea +82</span>
              <Image src="/icons/chevron-right-gray.svg" alt="arrow-right" className="rotate-90" width={16} />
            </div>
          )}

          {isGlobal &&
            TAX_ID_CONFIG[countryValue] &&
            (() => {
              const taxIdConfig = TAX_ID_CONFIG[countryValue];
              return (
                <Input
                  label={taxIdConfig.label}
                  containerClassName="w-[280px]"
                  placeholder={`Enter ${taxIdConfig.label}`}
                  register={register('shipping_info_clearance_code', {
                    required: taxIdConfig.required,
                    validate: (value) => !value || taxIdConfig.validate(value)
                  })}
                />
              );
            })()}
        </div>

        {/* E-mail */}
        <div className="flex items-end relative">
          <Input
            label={
              <div className="flex items-center relative gap-2  !w-[calc(100vw-611px)]">
                {<span>{intl.formatMessage({ id: 'order_section1_input4_label', defaultMessage: 'E-mail' })}</span>}
                <div className="text-[13px] font-semibold text-[#808080]">
                  (
                  <FormattedMessage
                    id="email_input_desc"
                    defaultMessage="We will send an invoice for pick-up to the email address you provided"
                  />
                  )
                </div>
              </div>
            }
            containerClassName={`w-[320px]`}
            placeholder={'E-mail'}
            register={register('shipping_info_email_1', { required: true })}
          />
          <div className="h-[52px] flex items-center px-4 text-[14px] font-semibold">@</div>
          <Input
            containerClassName={`w-[248px] h-[52px]`}
            errorMessage={emailDomainErrorMessage}
            register={register('shipping_info_email_2')}
          />
          <div
            className="px-5 flex items-center justify-between border border-[#dadada] rounded-[10px] w-[248px] h-[52px] gap-8 cursor-pointer ml-2"
            onClick={() => setIsOptionVisible((pev) => !pev)}
          >
            <div>
              <span className="text-[14px] font-semibold">
                {emailDomainData.includes(domainValue) ? (
                  domainValue
                ) : (
                  <FormattedMessage id="email_domain_enter_manually" defaultMessage="Enter Manually" />
                )}
              </span>
              {isOptionVisible && (
                <ul className="w-[248px] h-[266px] absolute top-[100px] z-10 right-0 border border-[#dadada] overflow-y-scroll rounded-[10px] shadow-[0px_4px_10px_0px_#00000029] bg-white">
                  {emailDomainData.map((emailDomain) => (
                    <li
                      key={emailDomain}
                      className="bg-white text-black p-4 text-[14px] font-semibold cursor-pointer transition ease-in-out duration-300 hover:bg-[#c1c1c1]"
                      onClick={() => handleEmailDomainClick(emailDomain)}
                    >
                      {emailDomain === 'Enter manually' ? (
                        <FormattedMessage id="email_domain_enter_manually" defaultMessage="Enter Manually" />
                      ) : (
                        emailDomain
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Image
              src="/icons/arrow-down.svg"
              alt="arrow icon"
              className="w-[15px] h-[15px] transition ease-in-out duration-300 cursor-pointer"
              style={{ transform: isOptionVisible ? 'rotate(180deg)' : 'rotate(0)' }}
              width={15}
              height={15}
            />
          </div>
        </div>

        {/* 주소영역 국내/해외 분기처리 */}
        {isGlobal ? (
          <>
            <div className="flex flex-row gap-4 w-full">
              <div className="flex flex-col gap-2 flex-1">
                <span className="text-[14px] font-semibold" style={{ width: '279px' }}>
                  <FormattedMessage id="country_or_region" defaultMessage="Country or Region" />
                </span>
                <div className="bg-[#ededed] text-[#999999] border border-[#e0e0e0] w-full flex items-center px-5 rounded-[10px] flex-1 !h-[54px]">
                  {!shipping_info_country_code
                    ? 'Auto-set from phone number'
                    : shippingCountries.filter((item) => item.code === shipping_info_country_code)[0]?.name}
                </div>
              </div>

              <Input
                label={<FormattedMessage id="street_address_1" defaultMessage="Street Address 1" />}
                containerClassName="flex-1"
                placeholder={'e.g., Street name'}
                register={register('shipping_info_address_1', { required: true })}
              />
              <Input
                label={
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <FormattedMessage id="street_address_2" defaultMessage="Street Address 2" />
                    <small style={{ color: '#888' }}>
                      <FormattedMessage id="optional" defaultMessage="(Optional)" />
                    </small>
                  </div>
                }
                containerClassName="flex-1"
                placeholder={'e.g., Apt, Suite, Building name'}
                register={register('shipping_info_address_2')}
              />
            </div>
            <div className="flex flex-row gap-4 w-full">
              <Input
                label={<FormattedMessage id="city" defaultMessage="City" />}
                containerClassName="flex-1"
                placeholder={'e.g., Tokyo, Los Angeles'}
                register={register('shipping_info_city', { required: true })}
              />
              <Input
                label={<FormattedMessage id="state_province" defaultMessage="State / Province" />}
                containerClassName="flex-1"
                placeholder={'e.g., Tokyo, CA, '}
                register={register('shipping_info_state', { required: true })}
              />
              <Input
                label={<FormattedMessage id="zip_code" defaultMessage="ZIP code" />}
                containerClassName="flex-1"
                placeholder={'e.g., 100-0001, 90015'}
                register={register('shipping_info_postal_code', { required: true })}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-row items-end gap-[10px] w-full">
            <Input
              style={{ minWidth: '370px' }}
              onClick={addressModal.open}
              label={
                <div className="flex items-center gap-2">
                  <FormattedMessage id="address" defaultMessage="Address" />
                  <small style={{ color: '#888' }}>
                    <FormattedMessage id="must_be_entered_in_korean" defaultMessage="(Must be entered in Korean)" />
                  </small>
                </div>
              }
              containerClassName="flex-1"
              placeholder={'e.g., 서울 마포구 와우산로29길 48-14 3층'}
              register={register('shipping_info_address_1', { required: true })}
            />
            <Input containerClassName="flex-1" placeholder={'e.g., 1동 1004호'} register={register('shipping_info_address_2')} />
            <Button textColor="#262626" className="bg-[#f2f2f2] !rounded-[10px] !w-[120px] !h-[56px]" onClick={addressModal.open}>
              <span className="text-[16px]">
                <FormattedMessage id="search" defaultMessage="Search" />
              </span>
            </Button>
          </div>
        )}
        {/* 메모 */}
        <label className="flex flex-col gap-2">
          <div className="flex">
            <span className="text-[14px] font-semibold">
              <FormattedMessage id="delivery_instructions" defaultMessage="Delivery instructions" />
            </span>
            <small>
              <FormattedMessage id="optional" defaultMessage="(Optional)" />
            </small>
          </div>

          <textarea
            {...register('shipping_info_memo', { required: false })}
            placeholder="e.g., Leave at the front door"
            className="text-[14px] leading-5 font-semibold h-20 w-full p-3 text-[#333] bg-white border border-[#ddd] rounded-lg resize-none overflow-auto outline-none placeholder:text-[#dadada]"
          />
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-[14px] font-semibold">
            <FormattedMessage id="shipping_information_domestic" defaultMessage="Shipping Information" />
          </span>
          <span className="text-[13px] text-[#808080]">
            <FormattedMessage
              id={isGlobal ? 'shipping_information_global_1' : 'shipping_information_domestic_1'}
              defaultMessage={
                isGlobal
                  ? '· Your order will be shipped via Korea Post and could take up to 19 business days to arrive. '
                  : '· Your order will be shipped via Lotte Express and is expected to arrive within 5 business days.'
              }
            />
          </span>
          <span className="text-[13px] text-[#808080]">
            <FormattedMessage
              id={isGlobal ? 'shipping_information_global_2' : 'shipping_information_domestic_2'}
              defaultMessage={
                isGlobal
                  ? '· Your order will be shipped via Korea Post and could take up to 19 business days to arrive. '
                  : '· We’ll send your tracking number to your email once it’s ready.'
              }
            />
          </span>
        </div>
      </div>

      {/* 주소 선택 모달 */}
      {addressModal.isShowing && (
        <div className="fixed inset-0 w-full h-full bg-black/80 flex justify-center items-center z-[100]">
          <div className="w-[568px] max-h-[80vh] bg-white rounded-[16px] overflow-hidden flex flex-col">
            <div className="w-full flex justify-end bg-white pt-4 px-6 pb-6" onClick={addressModal.close}>
              <Image src="/icons/close.svg" alt="close" width={24} height={24} className="cursor-pointer" />
            </div>
            <DaumPostcodeEmbed onComplete={handleComplete} style={{ height: '60vh' }} hideMapBtn={true} />
          </div>
        </div>
      )}
      {receivingModal.isShowing && <ReceivingMethodModal onClose={receivingModal.close} />}
    </>
  );
};
